import React from 'react';
import { Helmet } from 'react-helmet-async';
import HomeLayout from '../../hocs/layout/HomeLayout';
import { Meta } from '../../components/navigation/Helmet';

function CookiePolicy() {
  return (
    <HomeLayout>
      <Meta title="Política de Cookies" />
      
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Política de Cookies
            </h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-6 text-center">
                <strong>Última actualización:</strong> 9 de septiembre de 2025
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. ¿Qué son las Cookies?</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita 
                  un sitio web. En <strong>TableChat</strong>, utilizamos cookies para mejorar su experiencia de usuario, 
                  personalizar nuestros servicios y analizar el uso de nuestra plataforma de pedidos sin meseros.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Tipos de Cookies que Utilizamos</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Cookies Esenciales</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Estas cookies son necesarias para el funcionamiento básico de TableChat y no pueden ser desactivadas:
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Autenticación:</strong> Mantienen su sesión activa después del login</li>
                    <li><strong>Seguridad:</strong> Protegen contra ataques CSRF y otras amenazas</li>
                    <li><strong>Carrito de pedidos:</strong> Mantienen sus selecciones durante la sesión</li>
                    <li><strong>Preferencias del sitio:</strong> Recuerdan configuraciones básicas</li>
                  </ul>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Cookies de Rendimiento</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Nos ayudan a entender cómo los usuarios interactúan con nuestra plataforma:
                </p>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Google Analytics:</strong> Análisis de tráfico y comportamiento de usuarios</li>
                    <li><strong>Hotjar:</strong> Mapas de calor y grabaciones de sesiones (anonimizadas)</li>
                    <li><strong>Métricas de rendimiento:</strong> Tiempo de carga y errores técnicos</li>
                    <li><strong>A/B Testing:</strong> Pruebas para mejorar la experiencia de usuario</li>
                  </ul>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Cookies de Funcionalidad</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Mejoran la funcionalidad y personalización de su experiencia:
                </p>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Idioma y región:</strong> Recuerdan su idioma preferido</li>
                    <li><strong>Restaurantes favoritos:</strong> Guardan sus preferencias alimentarias</li>
                    <li><strong>Historial de pedidos:</strong> Facilitan pedidos repetidos</li>
                    <li><strong>Configuraciones de accesibilidad:</strong> Mantienen ajustes personalizados</li>
                  </ul>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.4 Cookies de Marketing</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Solo se utilizan con su consentimiento explícito para personalizar anuncios:
                </p>
                <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-4">
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Facebook Pixel:</strong> Anuncios personalizados en redes sociales</li>
                    <li><strong>Google Ads:</strong> Remarketing y publicidad dirigida</li>
                    <li><strong>Seguimiento de conversiones:</strong> Medición de efectividad publicitaria</li>
                    <li><strong>Perfiles de interés:</strong> Ofertas y promociones relevantes</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Cookies de Terceros</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Algunos de nuestros socios también colocan cookies en su dispositivo:
                </p>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Propósito</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duración</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-700">Google Analytics</td>
                        <td className="px-6 py-4 text-sm text-gray-700">Análisis de uso</td>
                        <td className="px-6 py-4 text-sm text-gray-700">2 años</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-700">Stripe</td>
                        <td className="px-6 py-4 text-sm text-gray-700">Procesamiento de pagos</td>
                        <td className="px-6 py-4 text-sm text-gray-700">Sesión</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-700">Facebook</td>
                        <td className="px-6 py-4 text-sm text-gray-700">Publicidad personalizada</td>
                        <td className="px-6 py-4 text-sm text-gray-700">90 días</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-700">Hotjar</td>
                        <td className="px-6 py-4 text-sm text-gray-700">Análisis de experiencia</td>
                        <td className="px-6 py-4 text-sm text-gray-700">1 año</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Gestión de Cookies</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Configuración en Navegadores</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Puede controlar las cookies a través de la configuración de su navegador:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Chrome:</strong> Configuración → Privacidad y seguridad → Cookies</li>
                  <li><strong>Firefox:</strong> Opciones → Privacidad y seguridad → Cookies</li>
                  <li><strong>Safari:</strong> Preferencias → Privacidad → Cookies</li>
                  <li><strong>Edge:</strong> Configuración → Privacidad → Cookies</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Herramienta de Gestión de Cookies</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    TableChat proporciona una herramienta fácil de usar para gestionar sus preferencias de cookies:
                  </p>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
                    Gestionar Preferencias de Cookies
                  </button>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Opt-out de Cookies de Marketing</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Google Ads:</strong> <a href="https://www.google.com/settings/ads" className="text-blue-600 hover:text-blue-800">Configuración de anuncios de Google</a></li>
                  <li><strong>Facebook:</strong> <a href="https://www.facebook.com/ads/settings" className="text-blue-600 hover:text-blue-800">Configuración de anuncios de Facebook</a></li>
                  <li><strong>Opt-out general:</strong> <a href="http://www.aboutads.info/choices/" className="text-blue-600 hover:text-blue-800">Digital Advertising Alliance</a></li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Impacto de Deshabilitar Cookies</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Si decide deshabilitar ciertos tipos de cookies, algunas funcionalidades pueden verse afectadas:
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Sin Cookies Esenciales:</h4>
                    <ul className="list-disc pl-4 text-gray-700 text-sm space-y-1">
                      <li>No podrá mantener sesiones activas</li>
                      <li>Los pedidos no se guardarán</li>
                      <li>Problemas de seguridad</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Sin Cookies de Funcionalidad:</h4>
                    <ul className="list-disc pl-4 text-gray-700 text-sm space-y-1">
                      <li>Perderá preferencias guardadas</li>
                      <li>No recordará restaurantes favoritos</li>
                      <li>Configuraciones se restablecerán</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Cookies en Dispositivos Móviles</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  En nuestras aplicaciones móviles, utilizamos tecnologías similares a las cookies:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Identificadores de dispositivo:</strong> Para análisis y personalización</li>
                  <li><strong>Almacenamiento local:</strong> Para guardar preferencias offline</li>
                  <li><strong>Push notifications:</strong> Para actualizaciones de pedidos (con su permiso)</li>
                  <li><strong>Geolocalización:</strong> Para encontrar restaurantes cercanos (opcional)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Actualizaciones de esta Política</h2>
                <p className="text-gray-700 leading-relaxed">
                  Podemos actualizar esta política de cookies cuando introduzcamos nuevas tecnologías o 
                  cambiemos nuestras prácticas. Le notificaremos sobre cambios significativos y solicitaremos 
                  su consentimiento renovado cuando sea necesario.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Contacto</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Si tiene preguntas sobre nuestra política de cookies, puede contactarnos:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Email:</strong> cookies@tablechat.com</p>
                  <p className="text-gray-700"><strong>Teléfono:</strong> +1 (555) 123-4567</p>
                  <p className="text-gray-700"><strong>Chat en vivo:</strong> Disponible en nuestra plataforma</p>
                </div>
              </section>

              <div className="border-t border-gray-200 pt-8 mt-8 text-center">
                <p className="text-gray-600">
                  Al continuar utilizando TableChat, acepta el uso de cookies según se describe en esta política.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HomeLayout>
  );
}

export default CookiePolicy;
